import { setLocale as setStoreLocale } from 'actions/locale/locale';
import { NOOP } from 'constants/actions';
import { DEFAULT_LOCALE } from 'constants/config';
import { LOCALE_COOKIE_NAME } from 'constants/cookies';
import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { BrowserRepository as browser } from 'repositories/browser/browser';
import { EmbeddedRepository as embedded } from 'repositories/embedded/embedded';
import { useDispatch } from 'store/configureStore';
import { cookies } from 'utils/cookies/cookies';
import {
  getKeyFromLocaleCode,
  isRtlLocale,
  isValidLocale,
  locales,
  type SupportedLocalesKeys,
  type Translations,
} from 'utils/translations/translations';
import { windowUtils } from 'utils/windowUtils/windowUtils';
import EN from '../../utils/translations/locales/EN.json';

export interface TranslationContextInterface {
  locale: SupportedLocalesKeys;
  keys: Translations;
  fallbackKeys: Translations;
  setLocale: (locale: SupportedLocalesKeys) => void;
}

const loadLanguageFile = async (
  language: SupportedLocalesKeys,
): Promise<Translations> => {
  const key = locales[language];

  return await import(
    /* webpackChunkName: "locales/[request]" */ `utils/translations/locales/${key}.json`
  );
};

const defaultLanguageFile: Translations = EN;

const TranslationContext = createContext<TranslationContextInterface>({
  locale: DEFAULT_LOCALE as SupportedLocalesKeys,
  keys: defaultLanguageFile,
  fallbackKeys: defaultLanguageFile,
  setLocale: NOOP,
});

const getUrlLocale = (): string | null => {
  return new URLSearchParams(windowUtils.getSearch()).get('locale');
};

const getInitialLocale = (): SupportedLocalesKeys => {
  const embeddedLocale = embedded.getProp('locale', '') as string;
  const urlLocale = getUrlLocale() || '';
  const previousLocale =
    getKeyFromLocaleCode(cookies.read(LOCALE_COOKIE_NAME) || '') || '';

  if (isValidLocale(embeddedLocale)) {
    return embeddedLocale as SupportedLocalesKeys;
  }
  if (isValidLocale(urlLocale)) {
    return urlLocale as SupportedLocalesKeys;
  }
  if (isValidLocale(previousLocale)) {
    return previousLocale as SupportedLocalesKeys;
  }

  return DEFAULT_LOCALE;
};

const setLanguageInformation = (locale: string) => {
  document.documentElement.setAttribute('lang', locale);
  const isCookiePermanent = false;

  cookies.set(
    LOCALE_COOKIE_NAME,
    locale,
    isCookiePermanent,
    browser.getFlywireSubdomain(),
  );

  document.documentElement.setAttribute(
    'dir',
    isRtlLocale(locale) ? 'rtl' : 'ltr',
  );
};

function TranslationProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const initialLocale = useMemo(() => {
    const initialLocale = getInitialLocale();
    dispatch(setStoreLocale(initialLocale));
    setLanguageInformation(initialLocale);
    return initialLocale;
  }, [dispatch]);
  const [locale, setLocale] = useState(initialLocale);
  const fallbackKeys = defaultLanguageFile;
  const [keys, setKeys] = useState(fallbackKeys);

  const loadLanguage = async (locale: SupportedLocalesKeys) => {
    setKeys(await loadLanguageFile(locale));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  useEffect(() => {
    loadLanguage(locale);
  }, [locale]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: false positive
  const value: TranslationContextInterface = useMemo(
    () => ({
      keys,
      fallbackKeys,
      locale,
      setLocale: (locale: SupportedLocalesKeys) => {
        setLocale(locale);
        setLanguageInformation(locale);
        dispatch(setStoreLocale(locale));
      },
    }),
    [keys, fallbackKeys, locale, setLocale, dispatch],
  );

  if (!Object.keys(locales).includes(locale)) {
    setLocale(DEFAULT_LOCALE);
  }

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

export { TranslationContext, TranslationProvider };
